import Component from '@glimmer/component'
import { colors } from '@colors'
import BaseHighcharterComponent from 'teacher-ui-client/components/base/highcharter'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import { service } from '@ember/service'
import { isPresent } from '@ember/utils'
import { t, type IntlService } from 'ember-intl'
import { gt } from 'ember-truth-helpers'
import { InlineAlert, AlertType, InlineAlertTitle } from 'teacher-ui-client/components/inline-alert'

interface StudentDataItem {
  grade_position_adjusted: number
  rate: number
  first_name: string
  last_name: string
}

interface TrendlineItem {
  grade_position: number
  rate: number
}
interface ClassGradeAverage {
  grade_position: number
  rate: number
}

interface Signature {
  Element: HTMLDivElement
  Args: {
    /* determines which part of the chart (upper or lower) will be green, and other part to be red */
    higherIsBetter: boolean
    yAxisTitle: string
    trendline: TrendlineItem[]
    studentsData: StudentDataItem[]
    classGradeAverages: ClassGradeAverage[]
  }
}

enum SeriesNames {
  allStudents = 'All students on WL',
  individualStudents = 'Individual students',
  yourClass = 'Your class',
}

const chartColors = {
  lightGreen: '#e3f4e3d4',
  lightRed: '#f8dfe4d4',
}

export class VocabularyAnalyserChart extends Component<Signature> {
  @service gradeSets!: GradeSetsService

  get chartOptions(): Highcharts.Options {
    const { higherIsBetter, trendline, studentsData, classGradeAverages, yAxisTitle } = this.args

    const yAxis: Highcharts.YAxisOptions = {
      visible: true,
      gridLineWidth: 1,
      gridLineColor: '#c0c0c0',
      title: { text: yAxisTitle },
    }

    const xAxisFormatter = (pos: number) => this.gradeSets.findByPosition(pos).name

    const xAxis: Highcharts.XAxisOptions = {
      visible: true,
      gridLineWidth: 1,
      gridLineColor: '#c0c0c0',
      allowDecimals: false,
      startOnTick: true,
      endOnTick: true,
      min: 2,
      max: 8,
      title: { text: 'Year/Grade' },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          return typeof this.value === 'number' ? xAxisFormatter(this.value) : ''
        },
      },
    }

    const states = {
      hover: {
        animation: false,
      },
      inactive: {
        animation: false,
        opacity: 1,
      },
      normal: {
        animation: false,
      },
      select: {
        animation: false,
      },
    }

    return {
      chart: {
        type: 'areaspline',
        plotBackgroundColor: higherIsBetter ? chartColors.lightGreen : chartColors.lightRed,
        animation: false,
      },
      plotOptions: {
        series: {
          pointPlacement: 'on',
        },
        areaspline: {
          color: '#36ade1',
          fillColor: !higherIsBetter ? chartColors.lightGreen : chartColors.lightRed,
          threshold: null,
          marker: {
            lineWidth: 2,
            lineColor: '#36ade1',
            fillColor: '#36ade1',
          },
          states,
        },
        scatter: {
          states,
        },
        line: {
          states,
        },
      },
      title: {
        text: 'Comparative Student Performance',
        align: 'left',
        style: {
          fontSize: '2em',
        },
      },

      legend: {
        backgroundColor: '#FFFFFF',
        align: 'right',
        verticalAlign: 'top',
        floating: true,
        x: 0,
        y: 0,
        itemStyle: {
          fontSize: '1.2em',
        },
      },

      tooltip: {
        enabled: true,
        formatter: function (this: Highcharts.Point) {
          const _y = this.y?.toFixed(2) ?? 0
          if (this.series.name === SeriesNames.individualStudents.toString()) {
            return `<b>${this.name}</b><br/> ${_y}%`
          } else if (this.series.name === SeriesNames.yourClass.toString()) {
            return `<b>Class average</b><br/>${_y}%`
          }
          return `<b>${this.y}%</b>`
        },
        backgroundColor: colors.dustyBlack500,
        borderColor: colors.dustyBlack500,
        style: {
          color: colors.white,
        },
      },

      xAxis,
      yAxis,
      series: [
        {
          name: SeriesNames.allStudents,
          data: trendline.map((tl) => ({ x: tl.grade_position, y: tl.rate })),
          type: 'areaspline',
        },
        {
          type: 'scatter',
          name: SeriesNames.individualStudents,
          data: studentsData.map((s) => {
            return {
              x: s.grade_position_adjusted,
              y: s.rate,
              name: `${s.first_name} ${s.last_name}`,
            }
          }),
          marker: {
            radius: 8,
            symbol: 'triangle',
            fillColor: '#cb378c',
          },
        },
        {
          type: 'scatter',
          name: SeriesNames.yourClass,
          data: classGradeAverages.map((cga) => ({ x: cga.grade_position, y: cga.rate })),
          marker: {
            radius: 10,
            symbol: 'circle',
            fillColor: '#22a524',
          },
        },
      ],
    }
  }

  <template>
    <BaseHighcharterComponent data-test-vocabulary-analyser @options={{this.chartOptions}} ...attributes />
  </template>
}

function getRelativePercentage(a: number, b: number): number {
  if (b === 0) {
    return 0
  }
  return ((a - b) / b) * 100
}

interface AverageDeviationAlertsSignature {
  Args: {
    word?: string
    /* determines whether above or below average will cause an alert */
    higherIsBetter: boolean
    studentsData: StudentDataItem[]
    classGradeAverages: ClassGradeAverage[]
    trendline: TrendlineItem[]
  }
}

export class AverageDeviationAlerts extends Component<AverageDeviationAlertsSignature> {
  @service intl!: IntlService
  @service gradeSets!: GradeSetsService

  get deviationAlerts(): { type: AlertType; message: string }[] {
    const uniqueStudentGrades = Array.from(new Set(this.args.studentsData.map((s) => s.grade_position_adjusted)))
    return uniqueStudentGrades
      .map((sg) => {
        const gradeTrend = this.args.trendline.find((tl) => tl.grade_position === sg)
        const gradeAverage = this.args.classGradeAverages.find((cga) => cga.grade_position === sg)?.rate ?? 0

        if (gradeTrend) {
          const count = Math.abs(Math.round(getRelativePercentage(gradeAverage, gradeTrend.rate)))
          const gradeName = this.gradeSets.findByPosition(sg).fullName
          const { word } = this.args
          if (this.args.higherIsBetter) {
            switch (true) {
              case gradeAverage > gradeTrend.rate:
                return {
                  type: AlertType.Success,
                  message: this.intl.t('pages.wlVocabulary.alertMessages.languageGems.positive', {
                    count,
                    word,
                    gradeName,
                  }),
                }
              case gradeAverage < gradeTrend.rate:
                return {
                  type: AlertType.Critical,
                  message: this.intl.t('pages.wlVocabulary.alertMessages.languageGems.critical', {
                    count,
                    word,
                    gradeName,
                  }),
                }
              default:
                return {
                  type: AlertType.Warning,
                  message: this.intl.t('pages.wlVocabulary.alertMessages.languageGems.caution', { gradeName }),
                }
            }
          }
          switch (true) {
            case gradeAverage < gradeTrend.rate:
              return {
                type: AlertType.Success,
                message: this.intl.t('pages.wlVocabulary.alertMessages.overusedWords.positive', {
                  count,
                  word,
                  gradeName,
                }),
              }
            case gradeAverage > gradeTrend.rate:
              return {
                type: AlertType.Critical,
                message: this.intl.t('pages.wlVocabulary.alertMessages.overusedWords.critical', {
                  count,
                  word,
                  gradeName,
                }),
              }
            default:
              return {
                type: AlertType.Warning,
                message: this.intl.t('pages.wlVocabulary.alertMessages.overusedWords.caution', { gradeName }),
              }
          }
        }
      })
      .filter(isPresent)
  }

  <template>
    <div class="space-y-4">
      {{#each this.deviationAlerts as |classGrade|}}
        <InlineAlert @type={{classGrade.type}}>
          <InlineAlertTitle>
            {{classGrade.message}}
          </InlineAlertTitle>
        </InlineAlert>
      {{/each}}
    </div>
  </template>
}

interface AverageWordUsageSignature {
  Args: {
    message: string
    studentsData: StudentDataItem[]
    classGradeAverages: ClassGradeAverage[]
    /* show above average results, else below average results */
    showAboveAverage: boolean
  }
}

export class AverageWordUsage extends Component<AverageWordUsageSignature> {
  get students() {
    return this.args.studentsData
      .filter((student) => {
        const gradeAverage =
          this.args.classGradeAverages.find((cga) => cga.grade_position === student.grade_position_adjusted)?.rate ?? 0
        return this.args.showAboveAverage ? student.rate > gradeAverage : student.rate < gradeAverage
      })
      .map((student) => {
        const gradeAverage =
          this.args.classGradeAverages.find((cga) => cga.grade_position === student.grade_position_adjusted)?.rate ?? 0
        return {
          ...student,
          relativePercentage: Math.abs(Math.round(getRelativePercentage(student.rate, gradeAverage))),
        }
      })
  }

  <template>
    <div>
      {{#if (gt this.students.length 0)}}
        <h3 class="text-center">{{@message}}</h3>
        <ul>
          {{#each this.students as |student|}}
            <li>{{student.first_name}}
              {{student.last_name}}
              ({{student.relativePercentage}}%
              {{if @showAboveAverage (t "pages.wlVocabulary.above") (t "pages.wlVocabulary.below")}})</li>
          {{/each}}
        </ul>
      {{/if}}
    </div>
  </template>
}
